import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { useThreads } from '@liveblocks/react/suspense';
import { RoomProvider, ClientSideSuspense, LiveblocksProvider } from '@liveblocks/react/suspense';
import { GlobalRoomOperationsProvider } from './GlobalRoomOperationsManager';
import { RoomOperationsProvider } from './RoomOperationsProvider';
import { RoomRegistrar, RoomType } from './RoomRegistrar';
import { useMentionSuggestions } from 'components/editor/GridDndEditor/TiptapEditor/components/Comments/hooks/useMentionSuggestions';
import { apiDocumentServiceRepository } from '../services/repositories/implementations/ApiDocumentServiceRepository.ts';
import type { ContentType } from 'utils/getContentType';

export type ThreadWithOrigin = ReturnType<typeof useThreads>['threads'][number] & {
  __origin: RoomType;
};

const ThreadsContext = createContext<{ threads: ThreadWithOrigin[]; isLoading: boolean; contentId: string } | null>(null);
export const useMergedThreads = () => {
  const v = useContext(ThreadsContext);
  if (!v) throw new Error('useMergedThreads must be used under <MergedThreadsProvider/>');
  return v;
};

const getLiveblocksPublicRoomName = (contentId: string) => {
  return `content:${contentId}:${RoomType.PUBLIC}`;
};

const getLiveblocksPrivateRoomName = (contentId: string) => {
  return `content:${contentId}:${RoomType.PRIVATE}`;
};

export function MergedThreadsProvider({
  contentId,
  contentType,
  isPrivate,
  children,
  userEmail,
}: {
  contentId: string;
  contentType: ContentType;
  isPrivate: boolean;
  children: React.ReactNode;
  userEmail?: string;
}) {
  const PRIVATE = getLiveblocksPrivateRoomName(contentId);
  const PUBLIC = getLiveblocksPublicRoomName(contentId);

  const resolveMentionSuggestions = useMentionSuggestions({ isPrivate, contentId });
  const editorRepository = apiDocumentServiceRepository;

  const [internal, setInternal] = useState<ThreadWithOrigin[]>([]);
  const [external, setExternal] = useState<ThreadWithOrigin[]>([]);
  const [isInternalInitialized, setIsInternalInitialized] = useState(!isPrivate);
  const [isExternalInitialized, setIsExternalInitialized] = useState(false);

  const merged = useMemo(() => {
    return [...internal, ...external];
  }, [internal, external]);

  const liveblocksKey = useMemo(() => {
    return `${contentId}:${userEmail || ''}`;
  }, [contentId, userEmail]);

  const stableKeyRef = useRef(liveblocksKey);

  useEffect(() => {
    setInternal([]);
    setExternal([]);
    setIsInternalInitialized(!isPrivate);
    setIsExternalInitialized(false);
    stableKeyRef.current = liveblocksKey;
  }, [contentId, isPrivate, userEmail, liveblocksKey]);

  const isTransitioning = stableKeyRef.current !== liveblocksKey;

  const handleInternalThreadsChange = useCallback((threads: ThreadWithOrigin[]) => {
    setInternal(threads);
    setIsInternalInitialized(true);
  }, []);

  const handleExternalThreadsChange = useCallback((threads: ThreadWithOrigin[]) => {
    setExternal(threads);
    setIsExternalInitialized(true);
  }, []);

  return (
    <LiveblocksProvider
      key={liveblocksKey}
      authEndpoint={async () => {
        let response;
        if (isPrivate) {
          response = await editorRepository.authLiveblocksPrivate(contentId);
        } else {
          const payload = { user: { email: userEmail || '' } };
          response = await editorRepository.authLiveblocksPublic(contentId, payload);
        }
        return response;
      }}
      resolveMentionSuggestions={resolveMentionSuggestions}
    >
      <GlobalRoomOperationsProvider>
        {isPrivate && (
          <RoomProvider id={PRIVATE}>
            <RoomOperationsProvider roomType={RoomType.PRIVATE}>
              <ClientSideSuspense fallback={null}>
                <RoomRegistrar
                  roomType={RoomType.PRIVATE}
                  contentId={contentId}
                  contentType={contentType}
                  onChange={handleInternalThreadsChange}
                />
              </ClientSideSuspense>
            </RoomOperationsProvider>
          </RoomProvider>
        )}

        <RoomProvider id={PUBLIC}>
          <RoomOperationsProvider roomType={RoomType.PUBLIC}>
            <ClientSideSuspense fallback={null}>
              <RoomRegistrar
                roomType={RoomType.PUBLIC}
                contentId={contentId}
                contentType={contentType}
                onChange={handleExternalThreadsChange}
              />
            </ClientSideSuspense>
          </RoomOperationsProvider>
        </RoomProvider>

        <ThreadsContext.Provider
          value={{
            threads: isTransitioning ? [] : merged,
            isLoading: isTransitioning || (isPrivate ? !isInternalInitialized || !isExternalInitialized : !isExternalInitialized),
            contentId,
          }}
        >
          {children}
        </ThreadsContext.Provider>
      </GlobalRoomOperationsProvider>
    </LiveblocksProvider>
  );
}
